import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { RouteHistoryEntry } from '../types/route-history-entry';
import { RedactionService } from '../../security/redaction.service';
import { RouteMaskService } from '../../security/route-mask.service';
import { RingBuffer } from '../storage/ring-buffer';

@Injectable({
  providedIn: 'root',
})
export class RouteHistoryCollector {
  private readonly entries = new RingBuffer<RouteHistoryEntry>(20);
  private currentRoute: string | null = null;
  private hasRecordedInitialLoad = false;
  private started = false;
  private subscription?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly redactionService: RedactionService,
    private readonly routeMaskService: RouteMaskService,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.subscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nextRoute = this.routeMaskService.maskRoute(event.urlAfterRedirects);

        if (!this.hasRecordedInitialLoad) {
          this.record('initial-load', this.getReferrerRoute(), nextRoute);
          this.currentRoute = nextRoute;
          this.hasRecordedInitialLoad = true;
          return;
        }

        const previousRoute = this.currentRoute ?? this.getReferrerRoute();
        if (previousRoute === nextRoute) {
          return;
        }

        this.record('navigation', previousRoute, nextRoute);
        this.currentRoute = nextRoute;
      });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }

  clear(): void {
    this.entries.clear();
    this.currentRoute = null;
    this.hasRecordedInitialLoad = false;
  }

  getEntries(): readonly RouteHistoryEntry[] {
    return this.entries.toArray();
  }

  private record(source: RouteHistoryEntry['source'], previousRoute: string, currentRoute: string): void {
    this.entries.push({
      source,
      previousRoute,
      currentRoute,
      timestamp: new Date().toISOString(),
    });
  }

  private getReferrerRoute(): string {
    if (!document.referrer) {
      return 'direct';
    }

    const referrer = this.redactionService.redactUrl(document.referrer);
    if (!referrer.startsWith(window.location.origin)) {
      return 'external-referrer';
    }

    return this.routeMaskService.maskRoute(referrer.replace(window.location.origin, ''));
  }
}
